import { createContext, useContext, useState, useEffect } from 'react';

interface CartItem {
  id: number;
  titulo: string;
  categoria: string;
  precio: number;
  cantidad: number;
  stock: number;
}

interface CartContextProps {
  cartItems: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (itemId: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextProps | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem('carrito') || '[]');
    setCartItems(storedCart);
  }, []);

  const updateLocalStorage = (items: CartItem[]) => {
    localStorage.setItem('carrito', JSON.stringify(items));
  };

  const addItem = (item: CartItem) => {
    setCartItems((prevItems) => {
      const updatedCart = [...prevItems];
      const existingItemIndex = updatedCart.findIndex((i) => i.id === item.id);
      if (existingItemIndex !== -1) {
        const existingItem = updatedCart[existingItemIndex];
        if (existingItem.cantidad + 1 <= existingItem.stock) {
          updatedCart[existingItemIndex] = {
            ...existingItem,
            cantidad: existingItem.cantidad + 1,
          };
        } else {
          alert('No hay suficiente stock para agregar este producto.');
        }
      } else {
        if (item.cantidad <= item.stock) {
          updatedCart.push(item);
        } else {
          alert('No hay suficiente stock para agregar este producto.');
        }
      }
      updateLocalStorage(updatedCart); // Actualizar localStorage
      return updatedCart;
    });
  };

  const removeItem = (itemId: number) => {
    setCartItems((prevItems) => {
      const updatedCart = prevItems.filter((item) => item.id !== itemId);
      updateLocalStorage(updatedCart);
      return updatedCart;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('carrito');
  };

  return (
    <CartContext.Provider value={{ cartItems, addItem, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
